import { useEffect, useState, type FunctionComponent } from 'react';

import Button from '@helsenorge/designsystem-react/components/Button';
import {
  type FilterButtonProps,
  FilterButton,
  FilterButtonAndChipsWrapper,
  FilterDrawer,
  FilterOverviewLinkList,
  FilterOverviewSearch,
  FilterResultCountAndSortWrapper,
  FilterStateWrapper,
  getFilterChips,
} from '@helsenorge/designsystem-react/components/Filter';
import Icon from '@helsenorge/designsystem-react/components/Icon';
import PlusLarge from '@helsenorge/designsystem-react/components/Icons/PlusLarge';
import LinkList from '@helsenorge/designsystem-react/components/LinkList';
import Loader from '@helsenorge/designsystem-react/components/Loader';
import NotificationPanel from '@helsenorge/designsystem-react/components/NotificationPanel';

import { LazyToggleList } from './LazyToggleList';
import useClinicalTrails from './useClinicalTrials';
import { type ClinicalTrialsResources, useClinicalTrialsFilter } from './useClinicalTrialsFilter';
import { updateHitsRefLength } from '../../_helpers/refs';

export const CLINICAL_TRIALS_ID = 'studier';

interface ClinicalTrialsBlockProps {
  title: string;
  clinicalTrialsApiUrl: string;
  snowstormApiUrl: string;
  snomedCTBranch: string;
  filterResources: FilterResources;
  resources?: ClinicalTrialsBlockResources;
  trialsPerPage?: number;
}

interface ClinicalTrialsBlockResources extends ClinicalTrialsResources {
  loadingError?: string;
  singleClinicalTrial?: string;
  multipleClinicalTrials?: string;
  showMore?: string;
}

export type FilterResources = Required<NonNullable<FilterButtonProps['resources']>>;

export const ClinicalTrialsBlock: FunctionComponent<ClinicalTrialsBlockProps> = ({
  title,
  resources,
  trialsPerPage = 25,
  clinicalTrialsApiUrl,
  snowstormApiUrl,
  snomedCTBranch,
  filterResources,
}) => {
  const [page, setPage] = useState(1);

  const {
    kliniskeStudier: clinicalTrials,
    concept,
    loading,
    error,
  } = useClinicalTrails(snowstormApiUrl, snomedCTBranch, clinicalTrialsApiUrl, resources?.loadingError);
  const {
    filter,
    filterCategories,
    filterLabels,
    filteredClinicalTrials,
    drawer,
    getLabel,
    updateSearchQuery,
    toggleFilter,
    removeFilterValue,
    resetFilters,
    resetFilterCategory,
  } = useClinicalTrialsFilter(clinicalTrials, concept?.id, resources);

  const linkRefs = updateHitsRefLength<HTMLLIElement>(filteredClinicalTrials.length);
  const moreTrials = filteredClinicalTrials.length > page * trialsPerPage;
  const handleShowMoreTrials = (): void => {
    setPage(page + 1);
  };

  useEffect(() => {
    linkRefs[(page - 1) * trialsPerPage]?.current?.focus();
  }, [page, trialsPerPage]);

  if (loading) {
    return <Loader center />;
  }

  if (error || !clinicalTrials.length) {
    return <NotificationPanel variant="error">{error}</NotificationPanel>;
  }

  return (
    <div className="default-block-spacing">
      <FilterStateWrapper>
        <FilterButtonAndChipsWrapper
          filterButtonComponent={<FilterButton onClick={() => drawer.open()} resources={filterResources} />}
          filterChips={getFilterChips({
            filter,
            getLabel,
            onChipClick: key => (key === 'fritekst' ? drawer.open() : drawer.open(key)),
            onChipRemove: removeFilterValue,
            onOverflowChipClick: () => drawer.open(),
          })}
          resources={filterResources}
        />
        <FilterResultCountAndSortWrapper
          resultCount={
            <span role="alert" aria-live="polite">
              <strong>
                {filteredClinicalTrials.length}{' '}
                {filteredClinicalTrials.length === 1
                  ? (resources?.singleClinicalTrial ?? 'clinical trial')
                  : (resources?.multipleClinicalTrials ?? 'clinical trials')}
              </strong>
            </span>
          }
        />
      </FilterStateWrapper>

      <FilterDrawer drawer={drawer} onReset={resetFilters} resultCount={filteredClinicalTrials.length} resources={filterResources}>
        <FilterDrawer.Overview title={title}>
          <FilterOverviewLinkList
            filter={filter}
            getLabel={getLabel}
            links={filterCategories.map(({ key }) => ({ filterKey: key, title: filterLabels[key] }))}
            resources={filterResources}
          />
          <FilterOverviewSearch
            value={filter.filters.fritekst ?? ''}
            onChange={event => updateSearchQuery(event.target.value)}
            clearButtonProps={{ onClick: () => updateSearchQuery('') }}
            resources={filterResources}
          />
        </FilterDrawer.Overview>
        {filterCategories.map(({ key, options }) => (
          <FilterDrawer.View key={key} id={key} title={filterLabels[key]} onReset={() => resetFilterCategory(key)}>
            <LazyToggleList
              legend={filterLabels[key]}
              options={options}
              selected={filter.filters[key]}
              onToggle={option => toggleFilter(key, option)}
            />
          </FilterDrawer.View>
        ))}
      </FilterDrawer>

      <div className="row bg-neutral50 pt-7 pb-8">
        <div className="col-12 col-md-10 offset-md-1">
          {filteredClinicalTrials.length > 0 && (
            <LinkList color="neutral">
              {filteredClinicalTrials.slice(0, page * trialsPerPage).map((clinicalTrial, index) => (
                <LinkList.Link key={clinicalTrial.id} ref={linkRefs[index]} href={clinicalTrial.lenke}>
                  {clinicalTrial.tittel}
                </LinkList.Link>
              ))}
            </LinkList>
          )}
          {moreTrials && (
            <Button concept="normal" variant="outline" onClick={handleShowMoreTrials} wrapperClassName="mt-6 ml-3 ml-md-9">
              <Icon svgIcon={PlusLarge} />
              {resources?.showMore ?? 'Show more'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClinicalTrialsBlock;
